/**
 * Lightweight form validation helpers (no external dependency).
 *
 * Usage:
 *   const errors = validate({ username, email, password }, loginRules);
 *   if (Object.keys(errors).length > 0) { ... }
 */

export type ValidationErrors = Record<string, string>;
export type Rule = (value: string) => string | null;
export type RuleSet = Record<string, Rule[]>;

/** Run all rules for all fields and return a map of field → first error. */
export function validate(
  values: Record<string, string>,
  rules: RuleSet,
): ValidationErrors {
  const errors: ValidationErrors = {};
  for (const [field, fieldRules] of Object.entries(rules)) {
    const val = values[field] ?? "";
    for (const rule of fieldRules) {
      const msg = rule(val);
      if (msg) {
        errors[field] = msg;
        break; // first error only
      }
    }
  }
  return errors;
}

// ── Common rules ────────────────────────────────────────────

export const required =
  (label = "Ce champ"): Rule =>
  (v) =>
    v.trim() ? null : `${label} est requis.`;

export const minLength =
  (n: number, label = "Ce champ"): Rule =>
  (v) =>
    v.length >= n ? null : `${label} doit contenir au moins ${n} caractères.`;

export const maxLength =
  (n: number, label = "Ce champ"): Rule =>
  (v) =>
    v.length <= n ? null : `${label} ne doit pas dépasser ${n} caractères.`;

export const isEmail =
  (label = "Email"): Rule =>
  (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : `${label} n'est pas valide.`;

export const passwordStrength =
  (): Rule =>
  (v) => {
    if (v.length < 8) return "Le mot de passe doit contenir au moins 8 caractères.";
    if (!/[A-Z]/.test(v) && !/[0-9]/.test(v))
      return "Le mot de passe doit contenir au moins un chiffre ou une majuscule.";
    return null;
  };

// ── Pre-built rule sets ─────────────────────────────────────

export const loginRules: RuleSet = {
  username: [required("Nom d'utilisateur"), minLength(3, "Nom d'utilisateur")],
  password: [required("Mot de passe")],
};

export const registerRules: RuleSet = {
  username: [
    required("Nom d'utilisateur"),
    minLength(3, "Nom d'utilisateur"),
    maxLength(64, "Nom d'utilisateur"),
  ],
  email: [required("Email"), isEmail()],
  password: [required("Mot de passe"), passwordStrength()],
};

export const resetRequestRules: RuleSet = {
  email: [required("Email"), isEmail()],
};

export const resetPasswordRules: RuleSet = {
  password: [required("Nouveau mot de passe"), passwordStrength()],
};
